import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as subBranchesApi from './sub-branches.api';

import type {
  CreateSubBranchRequest,
  SubBranchListQuery,
  UpdateSubBranchRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: SubBranchListQuery) => ['sub-branches', 'list', query] as const,
  detail: (id: string) => ['sub-branches', 'detail', id] as const,
};

export const useSubBranchesList = (query: SubBranchListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => subBranchesApi.listSubBranches(query),
    staleTime: 30_000,
  });

export const useCreateSubBranch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSubBranchRequest) => subBranchesApi.createSubBranch(payload),
    onSuccess: (subBranch) => {
      qc.invalidateQueries({ queryKey: ['sub-branches', 'list'] });
      toast.success(`Added ${subBranch.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add sub-branch'),
  });
};

export const useUpdateSubBranch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSubBranchRequest }) =>
      subBranchesApi.updateSubBranch(id, payload),
    onSuccess: (subBranch) => {
      qc.invalidateQueries({ queryKey: ['sub-branches', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(subBranch.id) });
      toast.success('Sub-branch updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update sub-branch'),
  });
};

export const useDeleteSubBranch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => subBranchesApi.deleteSubBranch(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sub-branches', 'list'] });
      toast.success('Sub-branch deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete sub-branch'),
  });
};

export const useBulkDeleteSubBranches = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => subBranchesApi.bulkDeleteSubBranches(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sub-branches'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete sub-branches'),
  });
};

export const useBulkSetSubBranchesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      subBranchesApi.bulkSetSubBranchesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sub-branches'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update sub-branch status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
